import { Text } from "@microbeta/ui";
import type React from "react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdClose, MdFullscreen } from "react-icons/md";
import type { ImageItem } from "../../../../services/imageUtils";
import { ImageGallery } from "../../ImageGallery";
import { ResponsiveImage } from "../../ResponsiveImage/ResponsiveImage";
import styles from "./ImageCardsGrid.module.scss";

export interface ImageCardsGridProps {
  images?: string[];
  imageVariants?: (ImageItem | undefined)[];
  alt: string;
  onDelete?: (index: number) => void;
  className?: string;
  disabled?: boolean;
  aspectRatio?: "square" | "landscape" | "portrait" | "auto";
  showImageInfo?: boolean;
}

export const ImageCardsGrid: React.FC<ImageCardsGridProps> = ({
  images,
  imageVariants,
  alt,
  onDelete,
  className = "",
  disabled = false,
  aspectRatio = "landscape",
  showImageInfo = true,
}) => {
  const { t } = useTranslation();
  const [fullscreenIndex, setFullscreenIndex] = useState<number | null>(null);

  // Determine if we have variants or regular images
  const hasVariants = imageVariants && imageVariants.length > 0;
  const hasImages = images && images.length > 0;
  const totalImages = hasVariants
    ? imageVariants.length
    : hasImages
      ? images.length
      : 0;

  const handleFullscreenClick = useCallback((index: number) => {
    setFullscreenIndex(index);
  }, []);

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent, index: number) => {
      event.stopPropagation();
      if (onDelete) {
        onDelete(index);
      }
    },
    [onDelete]
  );

  const handleCloseFullscreen = useCallback(() => {
    setFullscreenIndex(null);
  }, []);

  if (totalImages === 0) {
    return null;
  }

  return (
    <>
      <div className={`${styles.gridContainer} ${className}`}>
        {Array.from({ length: totalImages }).map((_, index) => {
          const variant = hasVariants ? imageVariants[index] : undefined;
          const imageUrl = hasImages ? images[index] : undefined;
          const rawKey = variant?.original?.url || imageUrl;
          const imageKey =
            typeof rawKey === "string" ? rawKey : `item-${index}`;

          return (
            <div
              key={`image-${imageKey.split("/").pop()}-${index}`}
              className={`${styles.imageCard} ${
                styles[
                  `aspect${aspectRatio.charAt(0).toUpperCase() + aspectRatio.slice(1)}`
                ]
              }`}
            >
              <div className={styles.imageContainer}>
                {variant ? (
                  <ResponsiveImage
                    variants={variant}
                    alt={`${alt} - ${index + 1}`}
                    className={styles.image}
                    size="medium"
                    loading="lazy"
                  />
                ) : (
                  <img
                    src={imageUrl}
                    alt={`${alt} - ${index + 1}`}
                    className={styles.image}
                    loading="lazy"
                  />
                )}

                {/* Overlay with controls */}
                <div className={styles.overlay}>
                  <div className={styles.controls}>
                    {/* Fullscreen button */}
                    <button
                      type="button"
                      className={styles.controlButton}
                      onClick={() => handleFullscreenClick(index)}
                      disabled={disabled}
                      aria-label={t("imageCard.viewFullscreen")}
                      title={t("imageCard.viewFullscreen")}
                    >
                      <MdFullscreen size={20} />
                    </button>

                    {/* Delete button */}
                    {onDelete && (
                      <button
                        type="button"
                        className={`${styles.controlButton} ${styles.deleteButton}`}
                        onClick={(e) => handleDeleteClick(e, index)}
                        disabled={disabled}
                        aria-label={t("imageCard.deleteImage")}
                        title={t("imageCard.deleteImage")}
                      >
                        <MdClose size={20} />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Image info */}
              {showImageInfo && (
                <div className={styles.imageInfo}>
                  <Text variant="small" className={styles.imageIndex}>
                    {index + 1} / {totalImages}
                  </Text>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Fullscreen gallery */}
      {fullscreenIndex !== null && (
        <ImageGallery
          images={hasImages ? images : undefined}
          imageVariants={hasVariants ? imageVariants : undefined}
          alt={alt}
          aspectRatio="auto"
          showThumbnails={totalImages > 1}
          initialIndex={fullscreenIndex}
          onClose={handleCloseFullscreen}
        />
      )}
    </>
  );
};

export default ImageCardsGrid;
